import Backbone from "backbone";
import _ from "underscore";
import HosProcess, { availabilityNamesDictionary } from "../../services/hosProcess";
import { ListView, generateChecklist } from "./checklist";
import { Dialog } from "./dialog";
import { createElement, show, hide } from "../../utils/vanilla";
import StartOfDayDialog from "./startOfDayDialog";
import DriveView from "../../DriveView";
import UserHosRuleSet from "../../model/UserHosRuleSet";
import { getServerDate } from "../../utils/storage/date";
import { dateTimeComparator } from "../../utils/date";
import { handleApi } from "../../utils/error";
import { PageLevel } from "../../store/mobile/types";
import AvailabilityDisplay from "../framework/availabilityDisplay/availabilityDisplay";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import pageHTML from "./ruleSetChangeView.tmpl";
import exemptionDialogHTML from "./exemptionDialog.tmpl";
import availabilityPreviewDialogHTML from "./availabilityDisplay/availabilityPreviewDialog.tmpl";
import {
    AUSTRALIARULESETS, getCycleChange, getFilteredRuleSets, getOperatingZoneChange, getRuleSetById,
    getRuleSetIdByIndex, hasCustomStartOfDay, hasExemption, IHosRuleset, isInCanada, isStartOfDayAllowed,
    THosStatus,
    type IUserHosRuleSet
} from "hos-tools";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { getSplitSleeperRuleSetName, getLatestRuleSetsNames } from "../../utils/helpers";
import DutyStatusLog from "../../model/DutyStatusLog";
import { getCurrentVehicle } from "../../utils/device";
import { FeatureFlagName } from "../../utils/featureFlagName";

export class RuleSetChangeModel extends Backbone.Model {
    showingOtherRulesets = false;
    showingSplitSleeperRulesets = false;
}
export default class RuleSetChangeView extends DriveView<RuleSetChangeModel>{

    protected strings = [
        "You cannot switch to this ruleset until you complete a cycle reset for the previous {0} ruleset.",
        "{0} days",
        "{0} (start of day: {1})",
        "Cycle 1",
        "Cycle 2",
        "United States",
        "Canada South of 60",
        "Canada North of 60",
        "Show all",
        "Hide all",
        "h",
        "{minutes}m",
        "{0} Break duration",
        "{0} Break",
        "{0} Break required in"
    ];
    private readonly template = _.template(pageHTML);
    private readonly hosUtils: HosProcess;
    private readonly userHosRuleSet?: UserHosRuleSet;
    private readonly currentUser: DeepReadonly<IUser> | null;
    private readonly availabilityDisplay: AvailabilityDisplay;
    private ruleSetList: ListView | undefined;
    private recentRuleSetList: ListView | undefined;
    private splitSleeperRuleSetList: ListView | undefined;
    private annotationDialog: Dialog;
    private startOfDayDialog: StartOfDayDialog;
    private availabilityPreviewDialog: Dialog;
    private currentStartOfDay?: string;
    private unavailableRuleSetDialog: Dialog;
    private currentRuleSetId: string;
    private readonly otherRulesetHtml: HTMLDivElement;
    private readonly recentRulesetHtml: HTMLDivElement;
    private readonly splitSleeperRulesetHtml: HTMLDivElement;
    private newCycleChangeStatus: THosStatus | undefined = undefined;

    constructor(options) {
        super(options, {
            title: "Change ruleset",
            activeSection: "hos",
            pageLevel: PageLevel.TWO
        });
        this.hosUtils = this.app.hosUtils;
        this.currentUser = this.app.userService.getCurrentUser();
        this.userHosRuleSet = this.getUserHosRuleSet();
        this.availabilityDisplay = new AvailabilityDisplay();
        this.model = new RuleSetChangeModel();
        this.otherRulesetHtml = createElement("div", undefined, "other-rulesets-container");
        this.recentRulesetHtml = createElement("div", undefined, "recent-rulesets-container");
        this.splitSleeperRulesetHtml = createElement("div", undefined, "split-sleeper-rulesets-container");
    }

    render() {
        this.el.innerHTML = "";
        this.ruleSetList && this.ruleSetList.remove();
        this.recentRuleSetList && this.recentRuleSetList.remove();
        this.splitSleeperRuleSetList && this.splitSleeperRuleSetList.remove();
        this.currentRuleSetId = this.userHosRuleSet ? this.userHosRuleSet.hosRuleSet.id.replace("HosRuleSet", "") : "None";

        let validRuleSet = this.hosUtils.getValidRuleSet(this.currentRuleSetId);
        this.currentStartOfDay = this.userHosRuleSet && this.userHosRuleSet.startOfDay;

        this.translator.translate(Object.keys(availabilityNamesDictionary).map(type => availabilityNamesDictionary[type]))
            .then(() => this.createRuleSetList(validRuleSet))
            .then(() => this.display(this.template(this.model)))
            .then(() => this.appendRulesetLists())
            // we need to show or hide other rulesets on initial page render, that's why we need to pass an opposite value of showingOtherRulesets
            .then(() => this.showHideOtherRulesetsList(!this.model.showingOtherRulesets))
            .catch(handleApi);
        return this;
    }

    remove() {
        this.closeDialogs();
        this.ruleSetList && this.ruleSetList.remove();
        this.ruleSetList = undefined;
        this.recentRuleSetList && this.recentRuleSetList.remove();
        this.recentRuleSetList = undefined;
        this.splitSleeperRuleSetList && this.splitSleeperRuleSetList.remove();
        this.splitSleeperRuleSetList = undefined;
        super.remove();
        return this;
    }

    /**
     * Filters the list of rulesets provided by ELD jurisdiction (or Timezone) to prevent ELD drivers switching to an EWD ruleset and vice versa
     * @param rules The list of rulesets to be filtered
     */
    filterRulesetsForELDJurisdiction(rules: IIdDictionary<IHosRuleset>, currentUser: DeepReadonly<IUser> | null) {
        // If the user has an Australian timezone or the app is in EWD mode (per Redux), filter out all the US/Canada rulesets
        if (currentUser?.timeZoneId?.match(/Australia/gi) || this.app.store.getState().data.ELDJurisdiction === "EWD") {
            Object.keys(rules).forEach(key => {
                if (AUSTRALIARULESETS.indexOf(key) === -1) {
                    delete rules[key];
                }
            });

            // Otherwise filter out the EWD rulesets
        } else {
            Object.keys(rules).forEach(key => {
                if (AUSTRALIARULESETS.indexOf(key) !== -1) {
                    delete rules[key];
                }
            });
        }
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #other-rulesets-toggle": this.toggleOtherRulesets
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    toggleOtherRulesets() {
        this.showHideOtherRulesetsList(this.model.showingOtherRulesets);
        this.model.showingOtherRulesets = !this.model.showingOtherRulesets;
    }

    showHideOtherRulesetsList(isShowing: boolean) {
        let listElement = (document.getElementById("other-rulesets-list") as HTMLElement);
        let title = (document.getElementById("other-rulesets-title") as HTMLElement);
        let spanElement = (document.getElementById("other-rulesets-toggle")?.firstElementChild as HTMLElement);
        if (isShowing) {
            title && title.classList.remove("panel__border");
            listElement && hide(listElement);
            spanElement.textContent = this.getString("Show all");
        } else {
            title && title.classList.add("panel__border");
            listElement && show(listElement);
            spanElement.textContent = this.getString("Hide all");
        }
    }

    getCustomHosRuleSetObjects(rules: IIdDictionary<IHosRuleset>, currentRuleSetIndex: string, selectableRulesets: string[]) {
        return selectableRulesets.map(ruleIndex => {
            let ruleSetId: string = getRuleSetIdByIndex(ruleIndex);
            let validationResult = this.hosUtils.validateUserHosRuleSet(ruleSetId);
            let ruleSetToComplete: IUserHosRuleSet | undefined;
            let disabled = false;
            if (!validationResult.isValid && validationResult.ruleSetToComplete) {
                ruleSetToComplete = validationResult.ruleSetToComplete;
                if (ruleSetToComplete) {
                    let useLegacyTexasShortHaulRulesets = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION);
                    let ruleSetToCompleteConfig = getRuleSetById(ruleSetToComplete.hosRuleSet.id, useLegacyTexasShortHaulRulesets);
                    let ruleSetConfig = getRuleSetById(ruleSetId, useLegacyTexasShortHaulRulesets);
                    if (ruleSetToCompleteConfig
                        && ruleSetToCompleteConfig.config.cycle
                        && ruleSetConfig
                        && ruleSetConfig.config.cycle
                        && ruleSetToCompleteConfig.config.cycle.cycleDays !== ruleSetConfig.config.cycle.cycleDays) {
                        disabled = true;
                    }
                }
            }
            return {
                id: ruleIndex,
                title: rules[ruleIndex].name,
                selected: ruleIndex === this.hosUtils.getValidRuleSet(currentRuleSetIndex || "None"),
                disabled,
                ruleSetToComplete
            };
        });
    }

    getRulesetListView(rulesetHtml: HTMLDivElement, selectableRulesets: any) {
        return generateChecklist({
            items: selectableRulesets,
            isCheckable: true,
            labelledBy: "ruleset-change-title",
            beforeSelectCallback: item => {
                if (item.get("selected") || item.get("disabled")) {
                    let ruleSetToComplete = item.get("ruleSetToComplete");
                    item.get("disabled") && ruleSetToComplete && this.openUnavailableRuleSetDialog(ruleSetToComplete.hosRuleSet.id);
                    return false;
                } else {
                    let currentItem = this.ruleSetList ? this.ruleSetList.collection.findWhere({ "selected": true }) : undefined;
                    if (!currentItem) {
                        currentItem = this.recentRuleSetList ? this.recentRuleSetList.collection.findWhere({ "selected": true }) : undefined;
                    }
                    if (!currentItem) {
                        currentItem = this.splitSleeperRuleSetList ? this.splitSleeperRuleSetList.collection.findWhere({ "selected": true }) : undefined;
                    }
                    currentItem && currentItem.set({ "selected": false });
                    return true;
                }
            },
            selectCallback: () => this.ruleSetSelected(),
            context: this,
            translator: this.translator,
            el: rulesetHtml
        });
    }

    private createRuleSetList(currentRuleSetIndex: string) {
        let useLegacyTexasShortHaulRulesets = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION);
        let rules = getFilteredRuleSets(useLegacyTexasShortHaulRulesets);
        let rulesetsFromStorage = this.hosUtils.getUserHosRuleSets().toArray();
        let splitSleeperRulesetIndex = getSplitSleeperRuleSetName(rules, currentRuleSetIndex);
        let recentRulesetIndexes = rulesetsFromStorage ? getLatestRuleSetsNames(rulesetsFromStorage, 3, splitSleeperRulesetIndex) : [];
        this.model.showingSplitSleeperRulesets = splitSleeperRulesetIndex ? true : false;

        if (!splitSleeperRulesetIndex && recentRulesetIndexes?.length < 2) {
            this.model.showingOtherRulesets = true;
        }

        // If we're not in debug mode, filter the rulesets list
        if (!this.app.store.getState().data.debugMode) {
            this.filterRulesetsForELDJurisdiction(rules, this.currentUser);
        }
        let filteredOtherKeys = Object.keys(rules).filter(ruleIndex => ruleIndex !== "CarrierExemption" && ruleIndex !== splitSleeperRulesetIndex && recentRulesetIndexes.indexOf(ruleIndex) === -1);
        let otherSelectableRulesets = this.getCustomHosRuleSetObjects(rules, currentRuleSetIndex, filteredOtherKeys);

        let filteredRecentKeys = Object.keys(rules).filter(ruleIndex => ruleIndex !== splitSleeperRulesetIndex && recentRulesetIndexes.indexOf(ruleIndex) >= 0);
        let recentSelectableRulesets = this.getCustomHosRuleSetObjects(rules, currentRuleSetIndex, filteredRecentKeys);

        let filteredSplitKeys = Object.keys(rules).filter(ruleIndex => ruleIndex === splitSleeperRulesetIndex);
        let splitSleeperSelectableRulesets = this.getCustomHosRuleSetObjects(rules, currentRuleSetIndex, filteredSplitKeys);

        this.ruleSetList = this.getRulesetListView(this.otherRulesetHtml, otherSelectableRulesets);
        this.recentRuleSetList = this.getRulesetListView(this.recentRulesetHtml, recentSelectableRulesets);
        if (this.model.showingSplitSleeperRulesets) {
            this.splitSleeperRuleSetList = this.getRulesetListView(this.splitSleeperRulesetHtml, splitSleeperSelectableRulesets);
        }
    }

    private appendRulesetLists() {
        this.el.querySelector("#other-rulesets-list")?.appendChild(this.otherRulesetHtml);
        this.el.querySelector("#recent-rulesets")?.appendChild(this.recentRulesetHtml);
        this.el.querySelector("#split-sleeper-rulesets")?.appendChild(this.splitSleeperRulesetHtml);
    }

    private openAnnotationDialog(ruleSetId) {
        this.hideAnnotationWarning();

        this.annotationDialog = new Dialog("exempt-annotation", exemptionDialogHTML, this.translator, "Apply exemption today", [[
            {
                text: "Done",
                id: "button-apply-annotation",
                spinner: true,
                callback: () => {
                    let annotation = (document.getElementById("exemption-annotation") as HTMLInputElement).value.trim();

                    this.hideAnnotationWarning();

                    if (annotation.length < 4) {
                        this.showAnnotationWarning();
                        this.annotationDialog.waiting.stop();
                    } else {
                        this.annotationDialog.remove();
                        this.confirmRuleSetChange(ruleSetId, annotation);
                    }
                }
            }
        ]], {
            isNightMode: this.app.store.getState().data.nightMode,
            onCancelCallback: () => this.resetSelected()
        });

        this.annotationDialog.open().catch(handleApi);
    }

    private openUnavailableRuleSetDialog(ruleSetIdToComplete: string) {
        let ruleset = getRuleSetById(ruleSetIdToComplete);
        this.unavailableRuleSetDialog = new Dialog(
            "forbidden-ruleset",
            `<span class="noTranslate">
                ${ this.getString("You cannot switch to this ruleset until you complete a cycle reset for the previous {0} ruleset.")
                .replace("{0}", ruleset ? ruleset.name : "") }
            </span>`,
            this.translator,
            "Warning",
            [[
                {
                    text: "Ok",
                    id: "button-ok",
                    callback: dialog => dialog.remove()
                }
            ]],
            {
                isNightMode: this.app.store.getState().data.nightMode
            }
        );

        void this.unavailableRuleSetDialog.open();
    }

    private closeDialogs() {
        this.unavailableRuleSetDialog && this.unavailableRuleSetDialog.remove();
        this.annotationDialog && this.annotationDialog.remove();
        this.startOfDayDialog && this.startOfDayDialog.close();
        this.availabilityPreviewDialog && this.availabilityPreviewDialog.remove();
    }

    private showAnnotationWarning() {
        let element = (document.getElementById("exempt-annotation-warning") as HTMLElement);
        element && show(element);
    }

    private hideAnnotationWarning() {
        let element = (document.getElementById("exempt-annotation-warning") as HTMLElement);
        element && hide(element);
    }

    private resetSelected() {
        if (this.ruleSetList) {
            let currentSelected = this.ruleSetList.collection.findWhere({ selected: true });
            currentSelected && currentSelected.set("selected", false);
            let previousSelected = this.ruleSetList.collection.findWhere({ id: this.currentRuleSetId });
            previousSelected && previousSelected.set("selected", true);
        }
        if (this.recentRuleSetList) {
            let currentSelected = this.recentRuleSetList.collection.findWhere({ selected: true });
            currentSelected && currentSelected.set("selected", false);
            let previousSelected = this.recentRuleSetList.collection.findWhere({ id: this.currentRuleSetId });
            previousSelected && previousSelected.set("selected", true);
        }
        if (this.splitSleeperRuleSetList) {
            let currentSelected = this.splitSleeperRuleSetList.collection.findWhere({ selected: true });
            currentSelected && currentSelected.set("selected", false);
            let previousSelected = this.splitSleeperRuleSetList.collection.findWhere({ id: this.currentRuleSetId });
            previousSelected && previousSelected.set("selected", true);
        }
    }

    private ruleSetSelected() {
        let ruleSet = this.ruleSetList ? this.ruleSetList.collection.findWhere({ selected: true }) as any : undefined;
        if (!ruleSet) {
            ruleSet = this.recentRuleSetList ? this.recentRuleSetList.collection.findWhere({ selected: true }) as any : undefined;
        }
        if (!ruleSet) {
            ruleSet = this.splitSleeperRuleSetList ? this.splitSleeperRuleSetList.collection.findWhere({ selected: true }) as any : undefined;
        }
        if (ruleSet) {
            let ruleSetIndex: string = ruleSet.get("id");
            let validationResult = this.hosUtils.validateUserHosRuleSet(getRuleSetIdByIndex(ruleSetIndex));
            if (validationResult.isValid && hasExemption(ruleSetIndex)) {
                this.openAnnotationDialog(ruleSetIndex);
            } else if (hasCustomStartOfDay(ruleSetIndex)) {
                this.startOfDayDialog = new StartOfDayDialog({ translator: this.translator, isUserNightMode: this.app.store.getState().data.nightMode });
                let incompletedRuleSetReset = !validationResult.isValid && validationResult.ruleSetToComplete;
                // Rulesets without an allowed Start of Day are stored as 00:00:00 server-side, so fall back only when the
                // active ruleset cannot carry a non-default value. When the reset is blocked the dialog persists this value.
                let activeStartOfDay = isStartOfDayAllowed(this.currentRuleSetId) ? this.currentStartOfDay : undefined;
                let currentStartOfDay = activeStartOfDay || this.hosUtils.getLatestRulesetWithAllowedStartOfDay()?.startOfDay || null;
                this.startOfDayDialog.open(currentStartOfDay, !!incompletedRuleSetReset).then(startOfDay => {
                    this.startOfDayDialog.close();
                    this.confirmRuleSetChange(ruleSetIndex, undefined, startOfDay || undefined);
                }).catch(e => {
                    this.resetSelected();
                    e && handleApi(e);
                });
            } else {
                this.confirmRuleSetChange(ruleSetIndex);
            }
        } else {
            this.confirmRuleSetChange();
        }
    }

    private getDisplayString(key: string) {
        switch (key) {
            case "CanadaCycleOne":
                return this.getString("Cycle 1");
            case "CanadaCycleTwo":
                return this.getString("Cycle 2");
            case "OperatingZoneAmerica":
                return this.getString("United States");
            case "OperatingZoneCanadaSouthOf60":
                return this.getString("Canada South of 60");
            case "OperatingZoneCanadaNorthOf60":
                return this.getString("Canada North of 60");
            default:
                return "";
        }
    }

    private formatPossibleZoneOrCycleChange(oldRulesetId, newRulesetId) {
        let zoneCycleChangeString = "";
        let isEwd = AUSTRALIARULESETS.indexOf(oldRulesetId) > -1;

        let changedOperatingZone = getOperatingZoneChange(oldRulesetId, newRulesetId) || [];
        if (changedOperatingZone.length > 0) {
            zoneCycleChangeString += `<span><span class="noticeBox--heading">Zone:</span>
                                        <span class="noTranslate">${ isEwd ? "" : (`${ this.getDisplayString(changedOperatingZone[0]) } &rarr; `) }${ this.getDisplayString(changedOperatingZone[1]) }</span></span>`;
        }

        if (isInCanada(newRulesetId) && !this.hosUtils.isNoneCanadaRuleSet(newRulesetId) && !this.hosUtils.isAlbertaRuleSet(newRulesetId)) {
            let lastCanadianRuleset = this.hosUtils.getLatestCanadianRulesetWithCycle();
            let changedCycle = lastCanadianRuleset && getCycleChange(lastCanadianRuleset.hosRuleSet.id, newRulesetId) || [];
            if (changedCycle.length > 0) {
                zoneCycleChangeString += `<span><span class="noticeBox--heading">Cycle:</span>
                                            <span class="noTranslate">${ this.getDisplayString(changedCycle[0]) } &rarr; ${ this.getDisplayString(changedCycle[1]) }</span></span>`;
                this.newCycleChangeStatus = changedCycle[1];
            }
        }

        return zoneCycleChangeString;
    }

    private confirmRuleSetChange(ruleSetId?: string, userAnnotationString?: string, startOfDay?: string) {
        if (!ruleSetId || !(this.currentUser && (this.currentRuleSetId !== ruleSetId || this.currentStartOfDay !== startOfDay))) {
            return undefined;
        }

        let isEwdRuleSet = AUSTRALIARULESETS.indexOf(ruleSetId) > -1;
        let hasAvailability = !isEwdRuleSet && this.hosUtils.hasAvailability(ruleSetId);
        let ruleResult = hasAvailability ? this.hosUtils.getPreviewAvailabilityForNewRuleset(ruleSetId, startOfDay) : undefined;
        let latestLog = this.hosUtils.getLatestLogForUser(this.currentUser);
        let availabilityHtml = "";
        if (hasAvailability && ruleResult) {
            availabilityHtml = this.availabilityDisplay.getDisplay(
                ruleResult,
                ruleSetId,
                latestLog,
                this.getString("{0} days"),
                "HOS",
                undefined,
                false,
                this.getString("h"),
                this.getString("{minutes}m").replace("{minutes}", ""),
                this.getString("{0} Break duration"),
                this.getString("{0} Break"),
                this.getString("{0} Break required in")
            );
        }

        // should be pre-translated
        let violationErrors = hasAvailability ? this.hosUtils.checkIfInOrNearViolation(ruleResult, ruleSetId) : [];
        return (violationErrors.length ? Promise.all(violationErrors.map(violationError => violationError.getNoticeBoxMessage(this.translator))) : Promise.resolve([]))
            .then(violationMessages => {
                let violationWarnings = "";
                violationMessages.forEach(violationMessage => {
                    violationWarnings += violationMessage;
                });

                return this.translator.translate(this.hosUtils.getRuleSet(ruleSetId).name)
                    .then(translatedRuleSetName => {
                        let formatRuleSetName = () => {
                            if (startOfDay) {
                                return this.getString("{0} (start of day: {1})").replace("{0}", translatedRuleSetName).replace("{1}", startOfDay);
                            }
                            return translatedRuleSetName;
                        };

                        this.newCycleChangeStatus = undefined;
                        let templateStr = (_.template(availabilityPreviewDialogHTML))({
                            newRuleSet: formatRuleSetName(),
                            availabilityDisplayHtml: availabilityHtml,
                            hasAvailability,
                            isIgnoringLog: false,
                            exemptAnnotation: userAnnotationString ? ` (${ userAnnotationString })` : "",
                            violationWarnings,
                            zoneOrCycleChanges: isEwdRuleSet ? "" : this.formatPossibleZoneOrCycleChange(this.currentRuleSetId, ruleSetId)
                        });

                        this.availabilityPreviewDialog = new Dialog(
                            "confirm-ruleset-change",
                            `<div id="select-ruleset-preview">${ templateStr }</div>`,
                            this.translator,
                            "Availability preview",
                            [{
                                text: "Change ruleset",
                                id: "select-ruleset-done",
                                callback: () => this.saveRuleSetChange(ruleSetId, userAnnotationString, startOfDay),
                                spinner: true
                            }],
                            {
                                isNightMode: this.app.store.getState().data.nightMode,
                                onCancelCallback: () => this.resetSelected()
                            }
                        );
                        return this.availabilityPreviewDialog.open();
                    });
            })
            .catch(handleApi);
    }

    private saveRuleSetChange(ruleSetId: string, userAnnotationString?: string, startOfDay?: string) {
        return new Promise<void>((resolve, reject) => {
            let user = this.currentUser;
            let resolveCallback = () => {
                this.availabilityPreviewDialog.remove();
                this.app.router.navigate("hos", true);
                resolve();
            };
            if (user && (this.currentRuleSetId !== ruleSetId || this.currentStartOfDay !== startOfDay)) {
                let currentDateTime = getServerDate();

                let ruleSet = new UserHosRuleSet({
                    id: "",
                    hosRuleSet: { id: `HosRuleSet${ ruleSetId }` },
                    user: { id: user.id },
                    dateTime: currentDateTime.toISOString(),
                    startOfDay: startOfDay
                });

                this.app.sync.addUserHosRuleSet(user, ruleSet, userAnnotationString)
                    .then(() => {
                        // Signal to footer menu that ruleset has been updated.
                        dispatcher.trigger(DispatcherEvents.UserUpdateRuleset);
                        if (this.newCycleChangeStatus) {
                            const currentVehicle = getCurrentVehicle();
                            // Create a pseudo cycle change log for the prompt service
                            currentVehicle && dispatcher.trigger(DispatcherEvents.WarnOmittedDataOnDSL, new DutyStatusLog({
                                id: "",
                                status: this.newCycleChangeStatus,
                                dateTime: ruleSet.dateTime,
                                driver: ruleSet.user,
                                origin: "Manual",
                                device: { id: currentVehicle.id }
                            }));
                        }
                    })
                    .then(() => {
                        resolveCallback();
                    })
                    .catch(e => {
                        this.availabilityPreviewDialog.remove();
                        handleApi(e);
                        reject(e);
                    });
            } else {
                resolveCallback();
            }
        });
    }

    private getUserHosRuleSet(): UserHosRuleSet | undefined {
        let userHosRuleSets = this.hosUtils.getUserHosRuleSets().toArray();
        if (!userHosRuleSets) {
            return undefined;
        }
        userHosRuleSets.sort(dateTimeComparator(false));
        return userHosRuleSets[0];
    }
}

